import { useState } from 'react';
import type { ReactElement } from 'react';
import { Button, Input, Modal, Textarea, cn, useI18n } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import type { AdminAssetRow } from '@xdocs/schemas';
import { safeT } from '../../../i18n/safeT.js';
import { useWs } from '../../../ws/WsProvider.js';
import { useAssetsT } from './useAssetsT.js';
import styles from './AssetActionDialog.module.css';

export interface AssetActionDialogProps {
  asset: AdminAssetRow;
  mode: 'unpublish' | 'remove';
  onClose: () => void;
  onDone: (assetId: string) => void;
}

/**
 * F60 unpublish/remove — both `admin.assets.*` ops REQUIRE a `reason`
 * (schema-enforced, `packages/schemas/src/admin.ts`). `remove` is the
 * severe, harder-to-reverse action so it additionally demands a TYPED
 * confirmation of the asset's exact title (ux.md: "High-stakes/irreversible
 * → typed confirmation... not a bare confirm button") — the shared
 * `<ConfirmDialog>` in `@xdocs/ui` doesn't support a reason field or a typed
 * input, so this is a small local composition of `Modal` + `Textarea` +
 * `Input` rather than a `@xdocs/ui` change (out of this task's ownership).
 * `unpublish` is reversible (F60 AC: "auditable BOTH directions") so it only
 * requires the reason, not the typed title.
 */
export function AssetActionDialog({ asset, mode, onClose, onDone }: AssetActionDialogProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useAssetsT();
  const { wsClient } = useWs();
  const [reason, setReason] = useState('');
  const [typedTitle, setTypedTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiresTypedConfirm = mode === 'remove';
  const typedMismatch = requiresTypedConfirm && typedTitle.length > 0 && typedTitle !== asset.title;
  const canSubmit = reason.trim().length > 0 && (!requiresTypedConfirm || typedTitle === asset.title);

  async function handleConfirm(): Promise<void> {
    setIsSubmitting(true);
    setError(null);
    try {
      // `admin.assets.unpublish` only accepts `type: 'document'|'share'`
      // (`packages/schemas/src/admin.ts`) — `AssetsPage` never offers the
      // Unpublish action on a `file` row, this guard only narrows the type
      // for the request payload below.
      if (mode === 'unpublish' && asset.type !== 'file') {
        await requestTyped(wsClient, 'admin.assets.unpublish', { type: asset.type, id: asset.id, reason });
      } else if (mode === 'remove') {
        await requestTyped(wsClient, 'admin.assets.remove', { type: asset.type, id: asset.id, reason });
      }
      onDone(asset.id);
    } catch (err) {
      setError(safeT(locale, (err as { message?: string }).message ?? 'errors:code.INTERNAL'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t(mode === 'unpublish' ? 'adminAssets:unpublish.title' : 'adminAssets:remove.title')} size="sm">
      <div className={cn(styles['body'])}>
        <p className={cn(styles['description'])}>{t(mode === 'unpublish' ? 'adminAssets:unpublish.description' : 'adminAssets:remove.description')}</p>
        <p className={cn(styles['assetTitle'])}>“{asset.title}”</p>

        <Textarea
          label={t('adminAssets:reason.label')}
          placeholder={t('adminAssets:reason.placeholder')}
          required
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />

        {requiresTypedConfirm && (
          <Input
            label={t('adminAssets:typedConfirm.label')}
            required
            value={typedTitle}
            onChange={(event) => setTypedTitle(event.target.value)}
            {...(typedMismatch ? { error: t('adminAssets:typedConfirm.mismatch') } : {})}
          />
        )}

        {error && (
          <p role="alert" className={cn(styles['error'])}>
            {error}
          </p>
        )}

        <div className={cn(styles['actions'])}>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('adminAssets:action.cancel')}
          </Button>
          <Button variant="destructive" state={isSubmitting ? 'loading' : 'idle'} disabled={!canSubmit} onClick={() => void handleConfirm()}>
            {t('adminAssets:action.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
